import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

export function ImageMarkerConnectLine({ activeMarker, containerRef, arrowColor = '#00ff66' }) {
  const [linePath, setLinePath] = useState('');
  const [startPoint, setStartPoint] = useState({ x: 0, y: 0 });
  const [arrows, setArrows] = useState([]);

  const pathRef = useRef(null);
  const animFrameRef = useRef(null);

  useEffect(() => {
    const calculatePath = () => {
      // 모바일에서는 연결선과 화살표를 숨김
      if (window.matchMedia('(max-width: 768px)').matches) {
        setLinePath('');
        setArrows([]);
        return;
      }
      
      if (!activeMarker) {
        setLinePath('');
        setArrows([]);
        return;
      }

      const targetId = activeMarker.mId || `marker-${activeMarker.id}`;
      const markerEl = document.getElementById(targetId);

      if (!markerEl) {
        setLinePath('');
        return;
      }

      // ⚡ 1. PSV가 화면 밖 마커에 부여하는 클래스/style(opacity, display 등) 체크
      const computedStyle = window.getComputedStyle(markerEl);
      if (
        computedStyle.display === 'none' ||
        computedStyle.visibility === 'hidden' ||
        computedStyle.opacity === '0' ||
        markerEl.classList.contains('psv-marker--hidden') // PSV 마커 숨김 클래스
      ) {
        setLinePath('');
        setArrows([]);
        return;
      }

      // ⚡ 2. 마커의 실제 화면 바운딩 박스가 뷰어 컨테이너(화면) 밖으로 나갔는지 체크
      const markerRect = markerEl.getBoundingClientRect();

      if (containerRef?.current) {
        const containerRect = containerRef.current.getBoundingClientRect();

        // 마커 중심점이 뷰어 영역(좌, 우, 위, 아래)을 벗어났다면 선 숨김
        const markerCenterX = markerRect.left + markerRect.width / 2;
        const markerCenterY = markerRect.top + markerRect.height / 2;

        const isOutOfContainer =
          markerCenterX < containerRect.left ||
          markerCenterX > containerRect.right ||
          markerCenterY < containerRect.top ||
          markerCenterY > containerRect.bottom;

        if (isOutOfContainer) {
          setLinePath('');
          setArrows([]);
          return;
        }
      }

      // ----------------------------------------------------
      // 아래는 기존 시작점/도착점 좌표 계산 및 path 생성 로직 동일
      // ----------------------------------------------------
      const tooltipEl =
        document.querySelector('.psv-tooltip') ||
        document.querySelector('.map_pin') ||
        markerEl.querySelector('.map_pin');

      let startX, startY;

      if (tooltipEl) {
        const tooltipRect = tooltipEl.getBoundingClientRect();
        startX = tooltipRect.left + tooltipRect.width / 2;
        startY = tooltipRect.top;
      } else {
        startX = markerRect.left + markerRect.width / 2;
        startY = markerRect.top;
      }
      startX = startX - 20;

      let endX = startX + 200;
      let endY = startY - 50;

      if (containerRef?.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        endX = containerRect.right - 350;
        endY = containerRect.top + 35;
      }

      const cornerRadius = 16;
      let pathData = '';

      if (startY > endY) {
        pathData = `
          M ${startX} ${startY}
          L ${startX} ${endY + cornerRadius}
          Q ${startX} ${endY} ${startX + cornerRadius} ${endY}
          L ${endX} ${endY}
        `.trim();
      } else {
        pathData = `M ${startX} ${startY} L ${endX} ${startY} L ${endX} ${endY}`;
      }

      setStartPoint({ x: startX, y: startY });
      setLinePath(pathData);
    };

    calculatePath();

    window.addEventListener('resize', calculatePath);
    window.addEventListener('scroll', calculatePath, true);

    let trackingFrameId;
    const loopCalculate = () => {
      calculatePath();
      trackingFrameId = requestAnimationFrame(loopCalculate);
    };
    trackingFrameId = requestAnimationFrame(loopCalculate);

    return () => {
      window.removeEventListener('resize', calculatePath);
      window.removeEventListener('scroll', calculatePath, true);
      cancelAnimationFrame(trackingFrameId);
    };
  }, [activeMarker, containerRef]);

  // 4. 화살표 애니메이션 (원 소스 로직 100% 동일)
  useEffect(() => {
    if (
      window.matchMedia('(max-width: 768px)').matches ||
      !linePath ||
      !pathRef.current
    ) {
      setArrows([]);
      return;
    }

    const pathEl = pathRef.current;
    const totalLength = pathEl.getTotalLength();
    const spacing = 20; // 화살표 간격
    const speed = 2.3; // 이동 속도

    let offset = 0;

    const animate = () => {
      offset = (offset + speed) % spacing;

      const newArrows = [];
      for (let len = offset; len < totalLength; len += spacing) {
        if (len <= 0) continue;

        const p1 = pathEl.getPointAtLength(len);
        const p2 = pathEl.getPointAtLength(Math.min(len + 1, totalLength));

        const angle = Math.atan2(p2.y - p1.y, p2.x - p1.x) * (180 / Math.PI);

        newArrows.push({
          x: p1.x,
          y: p1.y,
          angle: angle,
        });
      }

      setArrows(newArrows);
      animFrameRef.current = requestAnimationFrame(animate);
    };

    animFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
      }
    };
  }, [linePath]);

  // activeMarker가 지정되어 있지 않거나 경로가 없으면 아무것도 렌더링하지 않음
  if (!activeMarker || !linePath) return null;

  return ReactDOM.createPortal(
    <svg
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none',
        zIndex: 1,
      }}
    >
      <path
        ref={pathRef}
        d={linePath}
        fill="none"
        stroke="rgba(0, 255, 102, 0.25)"
        strokeWidth="1.5"
      />

      {arrows.map((arrow, idx) => (
        <polygon
          key={idx}
          points="-3.5,-3 4.5,0 -3.5,3"
          fill={arrowColor}
          transform={`translate(${arrow.x}, ${arrow.y}) rotate(${arrow.angle})`}
        />
      ))}

      <circle cx={startPoint.x} cy={startPoint.y} r="3" fill={arrowColor} />
    </svg>,
    document.body
  );
}